<template>
  <grid-item
    v-if="currentField"
    :ref="item.fieldGuid"
    :static="item.moved"
    :x="item.x"
    :y="item.y"
    :w="item.w"
    :h="item.h"
    :i="item.i"
    :max-h="ITEM_MAX_HEIGHT"
  >
    <template v-if="(isModal || isFilePreviewer) && currentField.displayInForm && currentField.currentUserPermissionLevel !== 'deny'">
      <input-widget
        v-if="currentField.dataTypeName !== 'lookup'"
        :id="`addEditRowModalField_${currentField.name}`"
        :ref="currentField.fieldGuid"
        :model="isEditMode ? dataInEditMode : defaultForm"
        :disabled="currentField.disabled || currentField.currentUserPermissionLevel === 'read' || isCalculatedLookup(currentField) || isAiCalculation(currentField)"
        class="field"
        :rls="currentField.isRLSField"
        :field="currentField"
        :skip-validation-rules="false"
        :date-format="getDateFormat"
      />
      <!-- @on-add-new-relational-entity="onAddNewRelationalEntity" -->
      <lookup-input-component
        v-else
        transit-ref="lkp-multiselect"
        :is-label-visible="true"
        :is-edit-modal="true"
        :disabled="currentField.disabled || currentField.currentUserPermissionLevel === 'read' || isCalculatedLookup(currentField)"
        :props-value="isEditMode ? dataInEditMode[currentField.name] : defaultForm[currentField.name]"
        :field="currentField"
        @on-select="onLookupChange"
        @on-add-new-relation="onAddNewRelationalEntity"
      />
    </template>

    <div
      v-if="!isModal && !isFilePreviewer"
      class="block__item"
    >
      <span
        class="field__name"
        v-html="formattedItemName"
      />
      <b-dropdown
        id="dropdown_context_menu"
        no-caret
        size="sm"
        variant="outlined"
        class="additional_functionality_btn"
      >
        <template #button-content>
          <font-awesome-icon :icon="['fas', 'ellipsis-v']" />
        </template>
        <b-dropdown-item @click="deleteFieldFromSection">
          Delete
        </b-dropdown-item>
        <b-dropdown-item
          v-if="data.Sections.length > 1"
          class="move-to__dropdown"
          @mouseover.native="onMouseOver"
          @mouseleave.native="onMouseLeave"
        >
          Move to
          <font-awesome-icon :icon="['fas', 'chevron-right']" />
          <additional-dropdown-menu
            v-if="showSectionsMenu"
            :container-id="layoutId"
            :field-guid="item.fieldGuid"
          />
        </b-dropdown-item>
      </b-dropdown>
    </div>
  </grid-item>
</template>

<script>

import {createNamespacedHelpers} from 'vuex';
import {GridItem} from 'vue-grid-layout';
import InputWidget from '@/core/components/complex-table/components/InputWidget.vue';
import AdditionalDropdownMenu from '@/modules/DataManagement/views/designer/components/AdditionalDropdownMenu.vue';
import {highlightText} from '@/core/utils';
import moment from 'moment/moment';
import LookupInputComponent from '@/core/components/LookupInputComponent.vue';

const {
  mapState: designerMapState,
  mapActions: designerMapActions,
} = createNamespacedHelpers('designer');

const {
  mapState: dataManagementMapState,
  mapActions: dataManagementMapActions,
} = createNamespacedHelpers('dataManagement');

const ITEM_MAX_HEIGHT = 2;

export default {
  name: 'DesignerGridLayoutItem',
  components: {
    LookupInputComponent,
    AdditionalDropdownMenu,
    InputWidget,
    GridItem,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    sectionName: {
      type: String,
      required: false,
    },
    layoutId: {
      type: [String, Number],
      required: false,
    },
    isFilePreviewer: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localDataInEditMode: {},
      currentField: null,
      fieldFormat: null,
      ITEM_MAX_HEIGHT,
      showSectionsMenu: false,
    };
  },
  // watch: {
  //   localDataInEditMode: {
  //     handler(val) {
  //       this.updateModel(JSON.parse(JSON.stringify(val)));
  //     },
  //     deep: true,
  //   },
  // },
  computed: {
    ...dataManagementMapState({
      fieldsTypesValues: state => state.fieldValues,
      permissionTypes: state => state.permissionTypes,
      tableSchema: state => state.table.schema,
    }),
    ...designerMapState({
      defaultForm: state => state.form,
      fields: state => state.fields,
      searchValue: state => state.searchValue,
      dataInEditMode: state => state.item,
      isEditMode: state => state.isEditMode,
      data: state => state.data,
      isModal: state => state.isModalOpened,
    }),
    getDateFormat() {
      const jsonData = typeof this.currentField.dataTypeJSON === 'string'
        ? JSON.parse(this.currentField.dataTypeJSON)
        : this.currentField.dataTypeJSON;
      const isDateField = this.currentField.dataTypeName === this.fieldsTypesValues.date;
      const format = jsonData.FormatString ? jsonData.FormatString : 'YYYY-MM-DD';

      return `${format}${isDateField ? '' : ' HH:mm:ss'}`;
    },
    formattedItemName() {
      return highlightText(this.searchValue, this.currentField.displayName);
    },
    isPostedStatusExist() {
      const postedValue = this.fields[this.tableSchema.postedStatusGuid];
      return this.isEditMode ? this.dataInEditMode[postedValue?.name] === 'Posted' : false;
    },
  },
  methods: {
    ...designerMapActions(['addToUnrelatedFields', 'updateModel']),
    ...dataManagementMapActions(['showAddNewRecordModal']),
    onLookupChange(field, selectedOption) {
      const data = this.isEditMode
        ? this.dataInEditMode
        : this.defaultForm;
      data[field.name] = selectedOption;
    },
    onMouseOver() {
      this.showSectionsMenu = true;
    },
    onMouseLeave() {
      this.showSectionsMenu = false;
    },
    deleteFieldFromSection() {
      this.addToUnrelatedFields({
        fieldUuid: this.item.fieldGuid.toLowerCase(),
        layoutId: this.layoutId,
      });
    },
    getLookupDefaultValue(item, field, isRelational = false) {
      if (field !== undefined) {
        const suggestionName = item[`${field.name}__$LookupStatus`];
        let lookupDefaultValue;
        const relationalField = field.data.lookup;
        if (item.customClass && this.isValidSuggestions(suggestionName)) {
          const suggestions = suggestionName.Suggestions || suggestionName.suggestions;
          lookupDefaultValue = suggestions[0].OriginalValue || suggestions[0].originalValue;
        }
        return isRelational ? {[relationalField.value]: lookupDefaultValue} : {[field.name]: lookupDefaultValue};
      }
    },
    async onAddNewRelationalEntity(item, index, field) {
      const primary = this.tableSchema.fields.find((f) => f.isPrimaryKey);
      const currentData = this.isEditMode ?   this.dataInEditMode : this.defaultForm;


      const params = {
        inline: true,
        record: {},
        todo: false,
        relationalTableId: field.relationalTableId,
        field: field,
        parentKey: this.modalKey,
        parentData: {...this.form},
        recordUuid: currentData[primary.name],
        entityUuid: field.entityGuid,
        defaultLookupValue: this.getLookupDefaultValue(currentData, field, true),
      };

      this.showAddNewRecordModal(params);
    },
    isCalculatedLookup(field) {
      if (field.dataTypeName === 'lookup') {
        let dataTypeJSON;

        if (field.dataTypeJSON) dataTypeJSON = JSON.parse(field.dataTypeJSON);

        return dataTypeJSON.IsCalculated;
      }
    },
    isAiCalculation(field) {
      return !!(field.dataTypeName === 'text' && field.calculation && field.calculation.fieldCalculationType === 'AIChat');
    },
  },
  beforeMount() {
    if(this.isEditMode) {
      this.localDataInEditMode = JSON.parse(JSON.stringify(this.dataInEditMode));
    }
    this.currentField = this.fields[this.item.fieldGuid.toLowerCase()];

    if (!this.currentField) return;
    if (this.currentField.currentUserPermissionLevel === this.permissionTypes.deny) {
      return false;
    }

    const jsonData = typeof this.currentField.dataTypeJSON === 'string'
      ? JSON.parse(this.currentField.dataTypeJSON)
      : this.currentField.dataTypeJSON;

    this.currentField.disabled = this.isPostedStatusExist;

    this.currentField.label = this.currentField.displayName;
    if (this.currentField.dataTypeName === 'decimalNumber'
        || this.currentField.dataTypeName === 'floatNumber'
        || this.currentField.dataTypeName === 'wholeNumber') {
      this.currentField.type = 'number';
      if (jsonData.IsPercentage) {
        this.currentField.placeholder = `${this.currentField.label}%`;
      }
    } else if (this.currentField.dataTypeName === 'dateTime') {
      this.currentField.type = 'datetime';
      this.currentField.placeholder = this.getDateFormat;
      if (this.isEditMode) {
        // this.localDataInEditMode[this.currentField.name] = moment(this.dataInEditMode[this.currentField.name]).format(this.getDateFormat);
        if(this.dataInEditMode[this.currentField.name] !== null) {
          this.dataInEditMode[this.currentField.name] = moment(this.dataInEditMode[this.currentField.name]).format(this.getDateFormat);
        }
      }
    } else if (this.currentField.dataTypeName === 'date') {
      this.currentField.type = 'date';
      this.currentField.placeholder = this.getDateFormat;
      if (this.isEditMode) {
        // this.localDataInEditMode[this.currentField.name] = moment(this.dataInEditMode[this.currentField.name]).format(this.getDateFormat);
        if(this.dataInEditMode[this.currentField.name] !== null) {
          this.dataInEditMode[this.currentField.name] = moment(this.dataInEditMode[this.currentField.name]).format(this.getDateFormat);
        }
      }
    } else if (this.currentField.dataTypeName === 'lookup') {
      const additionalData = this.tableSchema.fields.find(el => el.fieldGuid === this.item.fieldGuid);
      if (this.isEditMode) {
        const currentLookupValue = additionalData.data.options.find(el => el.id === this.dataInEditMode[this.currentField.name]);
        this.dataInEditMode[this.currentField.name] = currentLookupValue;
      }
      this.currentField.type = 'multi-select';
      this.currentField.config = additionalData.config;
      this.currentField.data = additionalData.data;
      this.currentField.relationalFieldId = additionalData.relationalFieldId;
      this.currentField.relationalTableId = additionalData.relationalTableId;
      this.currentField.trackChanges = additionalData.trackChanges;
      this.currentField.isTable = true;

    } else if (this.currentField.dataTypeName === 'boolean') {
      this.currentField.type = 'checkbox';
    } else if (this.tableSchema.postedStatusGuid === this.currentField.fieldGuid && this.currentField.dataTypeName === 'text') {
      this.currentField.type = 'select';
      this.currentField.disabled = !['admin', 'edit', 'unpost', 'post'].includes(this.tableSchema.currentUserPermissionLevel);
      if (!this.defaultForm[this.currentField.name]) {
        this.defaultForm[this.currentField.name] = 'Pending';
      }
    }
  },
};
</script>

<style lang="scss">
.field {

  & .multiselect__tags,
  & .multiselect {
    height: 35px;
    min-height: 32px;
  }

  & label {
    margin-bottom: 0;
    padding-bottom: 0 !important;
  }
}

.vue-grid-item:has(.hi) {
  pointer-events: none;
  color: red;
}

.inner_dropdown,
.block__item {
  display: flex;
  align-items: center;
}

.inner_dropdown {
  justify-content: space-between;
}

.block__item {
  border-radius: 6px;
  border: 2px solid #eee;
  height: 100%;
  justify-content: center;
  padding: 10px;
  text-align: center;

  .field__name {
    width: 100%;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  .additional_functionality_btn {
    position: absolute;
    top: 0;
    right: 0;

    .dropdown-item {
      padding-left: 13px;
    }

    .b-dropdown {
      width: 100%;

      & .btn {
        width: 100%;
      }
    }
  }
}

.move-to__dropdown {
  position: relative;

  a {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
}
</style>